import { useEffect } from 'react';
import { SpoolmarkShell, StudioShell } from './components/Chrome';
import { ArticlePage } from './pages/ArticlePage';
import { HomePage } from './pages/HomePage';
import { EldgardPage } from './pages/EldgardPage';
import { EldgardDocsPage, EldgardLegalPage } from './pages/EldgardLegalPage';
import { SITE_URL, getMetaTags, getRouteMetadata, normalizePath } from './seo';
import { JournalPage } from './pages/JournalPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { SpoolmarkPage } from './pages/SpoolmarkPage';
import {
  SpoolmarkDocsPage,
  SpoolmarkLegalPage,
} from './pages/SpoolmarkLegalPage';

function updateNamedMeta(
  selector: string,
  attributeName: 'name' | 'property',
  attributeValue: string,
  value?: string,
) {
  const element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!value) {
    element?.remove();
    return;
  }

  if (element) {
    element.content = value;
    return;
  }

  const meta = document.createElement('meta');
  meta.setAttribute(attributeName, attributeValue);
  meta.content = value;
  document.head.appendChild(meta);
}

function useRouteMetadata(path: string) {
  useEffect(() => {
    const page = getRouteMetadata(path);
    document.title = page.title;
    const tags = getMetaTags(page);
    for (const attribute of ['name', 'property'] as const) {
      for (const [key, value] of Object.entries(tags[attribute])) {
        updateNamedMeta(`meta[${attribute}="${key}"]`, attribute, key, value);
      }
    }

    const icon =
      document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon) {
      icon.href = page.icon;
      icon.type = page.iconType;
    }

    const touchIcon = document.head.querySelector<HTMLLinkElement>(
      'link[rel="apple-touch-icon"]',
    );
    if (touchIcon) touchIcon.href = page.touchIcon;

    const canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    if (page.noindex) canonical?.remove();
    else if (canonical) canonical.href = `${SITE_URL}${page.canonical}`;

    const scrollFrame = window.requestAnimationFrame(() => {
      let hash = window.location.hash.slice(1);
      try {
        hash = decodeURIComponent(hash);
      } catch {
        /* Keep malformed fragments harmless. */
      }
      const target = hash ? document.getElementById(hash) : null;

      if (target) {
        const previousScrollBehavior =
          document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        target.scrollIntoView({ block: 'start' });
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
      } else {
        window.scrollTo(0, 0);
      }
    });

    return () => window.cancelAnimationFrame(scrollFrame);
  }, [path]);
}

export default function App({ pathname }: { pathname?: string } = {}) {
  const path = normalizePath(
    pathname ??
      (typeof window === 'undefined' ? '/' : window.location.pathname),
  );
  useRouteMetadata(path);

  if (path === '/eldgard') return <EldgardPage />;
  if (path === '/eldgard/docs') return <EldgardDocsPage />;
  if (path === '/eldgard/privacy') {
    return <EldgardLegalPage document="privacy" />;
  }
  if (path === '/eldgard/terms') {
    return <EldgardLegalPage document="terms" />;
  }
  if (path === '/eldgard/support') {
    return <EldgardLegalPage document="support" />;
  }
  if (path === '/eldgard/data-deletion') {
    return <EldgardLegalPage document="data-deletion" />;
  }

  if (path === '/') {
    return (
      <StudioShell pathname={path}>
        <HomePage />
      </StudioShell>
    );
  }
  if (path === '/spoolmark') {
    return (
      <SpoolmarkShell pathname={path}>
        <SpoolmarkPage />
      </SpoolmarkShell>
    );
  }
  if (path === '/spoolmark/blog' || path === '/spoolmark/journal') {
    return (
      <SpoolmarkShell pathname={path}>
        <JournalPage />
      </SpoolmarkShell>
    );
  }
  if (path === '/spoolmark/privacy') {
    return (
      <SpoolmarkShell pathname={path}>
        <SpoolmarkLegalPage document="privacy" />
      </SpoolmarkShell>
    );
  }
  if (path === '/spoolmark/docs') {
    return (
      <SpoolmarkShell pathname={path}>
        <SpoolmarkDocsPage />
      </SpoolmarkShell>
    );
  }
  if (path === '/spoolmark/terms') {
    return (
      <SpoolmarkShell pathname={path}>
        <SpoolmarkLegalPage document="terms" />
      </SpoolmarkShell>
    );
  }
  if (path === '/spoolmark/support') {
    return (
      <SpoolmarkShell pathname={path}>
        <SpoolmarkLegalPage document="support" />
      </SpoolmarkShell>
    );
  }
  if (path === '/spoolmark/data-deletion') {
    return (
      <SpoolmarkShell pathname={path}>
        <SpoolmarkLegalPage document="data-deletion" />
      </SpoolmarkShell>
    );
  }
  if (path === '/spoolmark/blog/bambu-lab-spool-scanning-on-ios') {
    return (
      <SpoolmarkShell pathname={path}>
        <ArticlePage />
      </SpoolmarkShell>
    );
  }

  return (
    <StudioShell pathname={path}>
      <NotFoundPage />
    </StudioShell>
  );
}
